/** @module csi/table/Splitter */
define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"dojo/topic",
	"underscore",
	"csi/html",
	"csi/locale",
	"csi/printUtil",
	"csi/state",
	"csi/table",
	"csi/view/_Splitter"
], function(declare, domClass, topic, _, html, locale, printUtil, state, csiTable, _Splitter) {

	const SELECTOR_FOOT_LEFT = ".footLeft";
	const SELECTOR_FOOT_RIGHT = ".footRight";

	var ROW_NUMBER_DATA = "rowNumber";
	var PRIMARY_ROW_CLASS = "primaryRow";
	var DIVIDER_CLASS = "divider";
	var GROUP_CLASS = "tableViewGroup";
	var REPORT_CLASS = "tableViewReport";
	var DATA_SHEET_COMMENT_CLASS = "dataSheetComment";

	/**
	 * Return total height of rows in given range
	 * @param {number} start 
	 * @param {number} end 
	 * @param {number[]} rowHeights 
	 * @return {number}
	 */
	function _getRowHeightsInRange(start, end, rowHeights) {
		let n = 0;

		for (let i = start; i < end; ++i) {
			n += rowHeights[i];
		}

		return n;
	}

	/**
	 * Return minimum width required for parent content within the given height
	 * @property {DOMNode} parent
	 * @property {number} maxHeight
	 * @return {number}
	 */
	function _getMinWidthForPrimaryHeader(parent, maxHeight) {
		var printScale = Math.floor((state.get("printScale") || 1) * 100) + "%";
		var jParent = $(parent);
		var paddingAndBorder = jParent.outerWidth() - jParent.width();
		var jDiv = $("<div />");

		jDiv.html(jParent.html());
		jDiv.css("display", "inline-block");
		jDiv.css("font-size", printScale);
		jDiv.appendTo("body");

		var height = jDiv.height();
		var width = jDiv.width();

		if (height < maxHeight) {
			// matrix header should never be less than 10 pixels
			var lowerBound = 10;
			var upperBound = width;
			var split;
			var div = jDiv[0];

			// use binary search to find optimal width; stop once within 10 pixels - we don't need to be too precise
			while (upperBound - lowerBound > 10) {
				split = Math.round((upperBound + lowerBound) / 2);
				jDiv.width(split);
				height = jDiv.height();

				// if height is within max height and there is no width overflow, then use the split as the new upperBound
				if ((height <= maxHeight) && (div.scrollWidth <= jDiv.width())) {
					upperBound = split;
				} else {
					lowerBound = split;
				}
			}

			width = upperBound;
		}

		jDiv.remove();

		return width + paddingAndBorder;
	}

	/**
	 * @typedef {Object} _Header - wrapper for all data we need for each sub header when splitting table.
	 * @property {Boolean} bIsInMatrix -  tells if current sub header is in a matrix
	 * @property {Number} mainHeaderIndex - index of the primary header in matrix
	 * @property {Number} width - width of the sub header
	 * @property {Number} firstSubHeaderIndex - index of the first sub header in current matrix
	 */
	var _Header = declare(null, {
		bIsInMatrix: false,
		mainHeaderIndex: 0,
		width: 0,
		firstSubHeaderIndex: 0,

		constructor: function(bIsInMatrix, mainHeaderIndex, width, firstSubHeaderIndex) {
			this.bIsInMatrix = bIsInMatrix;
			this.mainHeaderIndex = mainHeaderIndex;
			this.width = width;
			this.firstSubHeaderIndex = firstSubHeaderIndex;
		}
	});

	/**
	 * @typedef {Object} _RowPageBoundary - holds beginning and end of a row
	 * @property {Number} beginning - tells splitter where to split this row for current page
	 * @property {Number} end - tells splitter where to split this row for current page
	 * @property {Boolean} bRemoveRow - if the page range is outside of this row's beginning and end, then we just need to have
	 * an empty row. This boolean tells splitter to just remove the row content completely.
	 */
	var _RowPageBoundary = declare(null, {
		beginning: 0,
		end: 0,
		bRemoveRow: false,

		constructor: function(beginning, end, bRemoveRow) {
			this.beginning = beginning;
			this.end = end;
			this.bRemoveRow = bRemoveRow;
		}
	});

	/**
	 * @typedef {Object} _Row - wrapper for all data we need for splitting table about rows.
	 * @property {_RowPageBoundary} rowPageBoundary - holds beginning and end of a row for each page.
	 * @property {Number} beginning - holds first cell index in current row. If is a matrix row, let's say
	 * the row starts from 3 column, this property will hold 3
	 * @property {Number} end - tells splitter where the row ends (useful for matrix rows)
	 * @property {Number} height - tells the row height
	 */
	var _Row = declare(null, {
		rowPageBoundary: [],
		beginning: 0,
		end: 0,
		height: 0,

		constructor: function(rowPageBoundary, beginning, end, height) {
			this.rowPageBoundary = rowPageBoundary;
			this.beginning = beginning;
			this.end = end;
			this.height = height;
		}
	});

	var Splitter = declare(_Splitter, {
		/**
		 * @property {number} HORIZONTAL_SPLIT_FULL_MATRIX_PCT - percentage of the page width at which a matrix should be forced
		 * to the next horizontal page (if the full matrix width will fit on that page)
		 */
		HORIZONTAL_SPLIT_FULL_MATRIX_PCT: 0.66,

		/** @property {number} MIN_MATRIX_WIDTH_PER_PAGE - minimum number of pixels to show in a matrix per page when it is split */
		MIN_MATRIX_WIDTH_PER_PAGE: 100,

		/** @property {number} _lastPrimaryRowHeight - height of first row of matrix if rendered individually */
		_lastPrimaryRowHeight: null,

		/** @property {Record<number, boolean>} _pageBoundaryRows - keep track of rows that should appear at top of subsequent pages */
		_pageBoundaryRows: null,

		/** @property {Number[]} colsPerPage - holds last index of a column in each page. */
		colsPerPage: null,

		/** @property {Number[]} widthPerPage - sum of column widths on each page, before being adjusted to match page width. */
		widthPerPage: null,

		/** @property {_Header[]} headers - holds meta data about all sub headers (if no matrix, then just headers). */
		headers: null,

		/** @property {number} minTableWidth - minimum width for table on a page */
		minTableWidth: null,

		/** @property {_Row[]} rowColumnMap - holds meta data about all rows (if matrix, then includes sub-rows as well). */
		rowColumnMap: null,

		/** @property {DOMNode[]} headerNodes - holds main header DOM nodes. */
		headerNodes: null,

		/** @property {number} pageLevel - number of level at which result rows are found */
		pageLevel: 0,

		/** @property {DOMNode[]} subHeaderNodes - holds sub header DOM nodes (for tables with matrix). */
		subHeaderNodes: null,

		/** @property {Number} totalResults - holds total number of rows in table. Not including Report, Comment and Data Sheet Comment rows. */
		totalResults: 0,

		/**
		 * @property {Number[]} frozenColIndex - holds index for all frozen columns. We can have Matrix columns among the frozen ones which we
		 * have to ignore. So this helps us to avoid repeating the check for matrix over and over.
		 */
		frozenColIndex: null,

		/**
		 * @property {Number} frozenColsWidth - holds total width of all frozen columns. The width is the outer width which includes paddings,
		 * margins and borders. It's calculated by adding up "width" css property on <col/> html element.
		 */
		frozenColsWidth: 0,

		constructor: function() {
			this.colsPerPage = [];
			this.widthPerPage = [];
			this.headers = [];
			this.rowColumnMap = [];
			this.rowMap = {};
			this.totalResults = 0;
			this.frozenColIndex = [];
			this.frozenColsWidth = 0;
			this._pageBoundaryRows = {};
		},

		/**
		 * table-specific split processing. It also sets the footer text for
		 * how many rows we are showing per page.
		 * @param {DOMNode[]} aPages
		 * @param {DOMNode} viewRoot
		 * @param {Number} nPageHeight - height of the page minus print margins.
		 * @param {Number} nPageWidth - width of the page minus print margins.
		 * @param {Function} [copyCallback]
		 */
		split: function(aPages, viewRoot, nPageHeight, nPageWidth, copyCallback) {
			const eContainer = this._getContainerNode(viewRoot);
			const table = eContainer.parentNode;

			// determine minimum width for table (on last horizontal page) to ensure that footer message is enclosed within the table;
			// double what is found on left (just to be safe) since footer results message at this point does not include a page range 
			// (i.e. "Displaying N results" as opposed to "Displaying 1-20 of N results"); if there is content on the right, then also
			// include some padding to separate the left from the right
			const widthFootLeft = $(table).find(SELECTOR_FOOT_LEFT).width() || 0;
			const widthFootRight = $(table).find(SELECTOR_FOOT_RIGHT).width() || 0;
			const widthFoot = (2 * widthFootLeft) + (widthFootRight ? (widthFootRight + 20) : 0);

			this.minTableWidth = Math.min(widthFoot, nPageWidth);
			this.pageLevel = csiTable.getResultLevel(viewRoot);
			this._fixTableLayout(table);
			this.inherited(arguments);

			// publish the count of rows and columns
			const numberOfRows = this._getRefNodes(eContainer).length;
			topic.publish("/csi/table/splitter/info", numberOfRows, this.numberOfColumns);

			// after pages are split, update table footer nodes with result counts
			this._updateTableFooters();
		},

		/**
		 * Fix table layout by setting widths explicitly on all header cells so that matrix column widths are preserved as well
		 * @param {DomNode} table
		 */
		_fixTableLayout: function(table) {
			const thead = $(table).find("> thead");
			const rows = $(table).find("> * > .csi-table-row");
			const tr = rows[0];
			const trSubs = (tr.firstChild.rowSpan > 1) ? rows[1] : null;
			const rowHeights = _.map(rows, (tr) => tr.offsetHeight);
			const headerWidths = [];

			_.each(tr.children, (th) => {
				if (trSubs && csiTable.isMatrixHeader(th)) {
					const sid = html.getDataAttribute(th, html.DATA_TABLE_SPEC_ID);

					html.queryDom([html.getDataAttributeSelector(html.DATA_TABLE_SPAN_SPEC_ID, sid)], trSubs).forEach((th) => {
						headerWidths.push(th.offsetWidth);
					});
				} else {
					headerWidths.push(th.offsetWidth);
				}
			});

			const colGroup = $("<colgroup></colgroup>");
			let total = 0;

			_.each(headerWidths, w => {
				const col = $("<col/>");
				col.css(html.WIDTH, w + html.PX);
				colGroup.append(col);
				total += w;
			});

			// set height for thead so rows maintain height when there are frozen columns or are split
			_.each(rows, (tr, i) => {
				$(tr).css(html.HEIGHT, rowHeights[i] + html.PX);
			});

			// remove existing colgroup (if any) and add new one
			$(table).find("> colgroup").remove();
			$(thead).before(colGroup);

			$(table).css({
				"tableLayout": "fixed",
				"width": total + html.PX
			});

			// store properties
			this.numberOfColumns = headerWidths.length;
			this.headerNodes = tr.childNodes;
			this.subHeaderNodes = trSubs ? trSubs.childNodes : null;
		},

		/**
		 * Return table body rows
		 * @param {DOMNode} eContainer - body of table
		 * @return {NodeList}
		 */
		_getRefNodes: function(eContainer) {
			return csiTable.getResultRows(eContainer);
		},

		_updateTableFooters: function() {
			var eFooter;
			var start;
			var end;
			var footerTextStore;
			var row;
			var tFoot;
			var footContent;
			var footContentWidth;
			var nHorizontalPages = this.colsPerPage.length;
			var containerNode;

			_.each(this.containers, function(container, index) {
				containerNode = $(container.containerNode);
				tFoot = containerNode.next("tfoot");

				if (!tFoot.length) {
					// some tables don't have footer, then we shouldn't handle them.
					return;
				}

				eFooter = tFoot.find(SELECTOR_FOOT_LEFT)[0];

				if (!eFooter.offsetHeight) {
					// footer is hidden, so ignore
					return;
				}

				if (index % nHorizontalPages == 0) {
					// this means we're on first page in horizontal pagination. So we need to construct the footer text.
					row = containerNode.find("tr." + PRIMARY_ROW_CLASS);
					if (row[0]) {
						start = html.getDataAttribute(row[0], ROW_NUMBER_DATA);
						end = html.getDataAttribute(row[row.length - 1], ROW_NUMBER_DATA);

						footerTextStore = locale.resource("SEARCH_DISPLAY_RESULTS", [start, end, this.totalResults]);
						html.setInnerText(eFooter, footerTextStore);
					} else {
						footerTextStore = eFooter.innerText;
					}
				} else {
					// we need to repeat the same footer as the first page in horizontal pagination.
					html.setInnerText(eFooter, footerTextStore);
				}

				if (index % nHorizontalPages == nHorizontalPages - 1) {
					// if it's the last page in horizontal pagination, then we need to set min-width for footer since
					// the footer width is not expanded to paper width. We need to make sure footer doesn't get cut off.
					footContent = tFoot.find(".footContent");

					footContentWidth = 10;
					_.each(footContent.children(), function(div) {
						footContentWidth += div.offsetWidth;
					});
					footContent.css("min-width", footContentWidth + "px");
				}
			}, this);
		},

		/**
		 * Determine if primary row plus first merged row can fit within the remaining space on the page
		 * @param {HTMLElement} primaryRefNode
		 * @param {number} secondRowHeight
		 * @param {number} remainingHeight
		 * @return {number}
		 */
		_twoMatrixRowsCannotFit(primaryRefNode, secondRowHeight, remainingHeight) {
			const minRowHeight = 16;

			if (secondRowHeight + minRowHeight >= remainingHeight) {
				return true;
			}

			const elementClone = primaryRefNode.cloneNode(true);
			$(elementClone).find("> td[rowspan]").attr("rowspan", 1);
			primaryRefNode.parentElement.appendChild(elementClone);
			const primaryRowHeight = elementClone.offsetHeight;
			primaryRefNode.parentElement.removeChild(elementClone);

			// store last calculated primaryRowHeight to be used while splitting up matrix row
			this._lastPrimaryRowHeight = primaryRowHeight;

			// use >= just to be sure page can fit content - don't want to cut it too close
			return primaryRowHeight + secondRowHeight >= remainingHeight;
		},

		_splitRowsVertically(refNodes, nPageHeight, otherPageHeightOverhead, firstPageHeightOverhead) {
			const {rowMap, _pageBoundaryRows} = this;
			const minMatrixHeightOnPage = 150;
			const rowHeights = refNodes.map((tr) => tr.offsetHeight);
			const rowMergedHeights = refNodes.map((tr, i) => tr.firstElementChild.offsetHeight);
			const subsequentAvailableHeight = nPageHeight - otherPageHeightOverhead;
			const bCanRemoveAllOnFirstPage = (otherPageHeightOverhead < firstPageHeightOverhead);
			let availableHeight = nPageHeight - firstPageHeightOverhead;
			let height = 0;
			let i = 0;
			let refNode;
			let previousCandidateRows = 0; // number of non-merged rows that should move to next page if current row is moved
			let previousTotalRows = 0; // number of merged and non-merged rows that should move to next page (see above)
			let totalResults = 0; // number of non-merged result rows
			let totalRows = 0; // number of non-merged rows

			// Loop through all rows and mark the rows that run over a page as separate rows
			while (refNode = refNodes[i]) {
				const {classList} = refNode;
				const rows = csiTable.getMergedRows(refNode);
				const nRows = rows.length;
				const fullRowHeight = rowMergedHeights[i];
				let movedToNextPage = false;

				// set class for main row so we can fetch it again for footer.
				const isGroup = classList.contains(GROUP_CLASS);
				const isReport = classList.contains(REPORT_CLASS);
				const isComment = classList.contains(DATA_SHEET_COMMENT_CLASS);
				let remainingHeight = availableHeight - height;

				if (fullRowHeight > remainingHeight) {
					// row does not fully fit on current page
					if (
						nRows === 1 ||
						(!i && bCanRemoveAllOnFirstPage) ||
						(remainingHeight < minMatrixHeightOnPage) ||
						this._twoMatrixRowsCannotFit(refNode, rowHeights[i + 1], remainingHeight)
					) {
						// whole row should be moved to next page if any of the below are true:
						//  1) row is not a matrix
						//  2) first merged row does not fit on the first page
						//  3) matrix row is less than minimum height for matrix
						//  4) at least 2 rows of matrix row do not fit
						//
						// also bring any previous groups and report rows to the next page
						_pageBoundaryRows[totalRows - previousCandidateRows] = true;
						height = _getRowHeightsInRange(i - previousTotalRows, i, rowHeights);
						availableHeight = remainingHeight = subsequentAvailableHeight - height;
						movedToNextPage = true;
					}
				}

				// track total rows result rows
				if (!isGroup && !isReport && !isComment) {
					classList.add(PRIMARY_ROW_CLASS);
					html.setDataAttribute(refNode, ROW_NUMBER_DATA, ++totalResults);
				}

				rowMap[totalRows++] = i;

				if (fullRowHeight > remainingHeight) {
					// matrix needs to be split
					let newFirstRow = refNode;
					let rowCount = 0;

					for (let j = i; j < i + nRows; j++) {
						if (height + rowHeights[j] > availableHeight) {
							const element = rows[j - i];

							if (refNode.childElementCount != element.childElementCount) {
								// convert merged row into primary row
								const parentRowClone = refNode.cloneNode(true);

								_.each(element.childNodes, function(tdElement) {
									const cellIndex = csiTable.getCellIndex(tdElement);
									const cell = csiTable.getCellAtIndex(parentRowClone, cellIndex);
									parentRowClone.replaceChild(tdElement.cloneNode(true), cell);
								});

								element.parentNode.replaceChild(parentRowClone, element);
								refNodes[j] = parentRowClone;

								$(newFirstRow).find("> td[rowspan]").attr("rowspan", rowCount);

								// there is now a new merged row, so reset reference and rowspans so remaining merged rows still appear merged
								// NOTE: this is needed for call to getCellIndex to work above if matrix is split again across pages
								newFirstRow = parentRowClone;
								$(newFirstRow).find("> td[rowspan]").attr("rowspan", i + nRows - j);

								// track new page boundary row
								_pageBoundaryRows[totalRows] = true;
								rowMap[totalRows++] = j;
								availableHeight = subsequentAvailableHeight;
								movedToNextPage = true;
							}

							height = 0;
							rowCount = 0;
						}

						// height of primary matrix row on its own could be larger than when it is part of matrix, so to be safe use the larger height
						height += (j === i) ? this._lastPrimaryRowHeight : rowHeights[j];
						rowCount++;
					}

					$(newFirstRow).find("> td[rowspan]").attr("rowspan", rowCount);

					// reset height to that of remaining matrix row
					height = newFirstRow.firstElementChild.offsetHeight;
				} else {
					// row fits within available space, so add height and move to next row
					height += fullRowHeight;
				}

				i += nRows;

				if (movedToNextPage) {
					// if we have just moved to a new page, then it cannot be carried forward again to the next page
					previousCandidateRows = previousTotalRows = 0;
				} else {
					// we want group and report rows to be on the same page as the rows following them, so keep track how many such rows exist in a row;
					// when we break the page we will move them along too. The same goes for rows that are followed by a comment row if they both can fit on the same page.
					const isNextRowComment = refNodes[i]?.classList.contains(DATA_SHEET_COMMENT_CLASS);

					if (isGroup || isReport || (isNextRowComment && (fullRowHeight + rowHeights[i] < availableHeight))) {
						++previousCandidateRows;
						previousTotalRows += nRows;
					} else {
						previousCandidateRows = previousTotalRows = 0;
					}
				}
			}

			this.totalResults = totalResults;
			this.nRows = totalRows;

			// add one final entry to help with loops
			rowMap[totalRows] = refNodes.length;
		},

		/**
		 * @override
		 * Build various lookup maps for item rows
		 * When the table is a whole, we need to build map to get the maximum height for each row before splitting.
		 */
		_buildRowMaps(refNodes, nPageHeight, otherPageHeightOverhead, firstPageHeightOverhead, nPageWidth) {
			this._splitRowsVertically(refNodes, nPageHeight, otherPageHeightOverhead, firstPageHeightOverhead);

			const {nRows, numberOfColumns, rowMap} = this;
			var beginning;
			var end;
			var pageBeginning;
			var pageEnd;
			var bRemoveRow;
			var innerRowBeginning;
			var innerRowEnd;
			var bIsInnerRow;
			var firstCell;

			for (let i = 0; i < nRows; ++i) {
				const refNode = refNodes[i];
				const startRow = rowMap[i];
				const endRow = rowMap[i + 1];

				for (let innerRowCounter = startRow; innerRowCounter < endRow; innerRowCounter++) {
					const innerRow = refNodes[innerRowCounter];
					beginning = 0;
					end = refNode.children.length - 1;

					bIsInnerRow = innerRow.children.length < numberOfColumns;

					if (bIsInnerRow) {
						beginning = csiTable.getCellIndex(innerRow.firstChild);
						end = csiTable.getCellIndex(innerRow.lastChild);
					}

					pageBeginning = 0;
					var rowPageBoundary = [];

					for (var page = 0; page < this.colsPerPage.length; page++) {
						bRemoveRow = false;

						pageEnd = this.colsPerPage[page];

						innerRowBeginning = pageBeginning;
						innerRowEnd = pageEnd;

						// check if the current row number of cells is less than overall number of cells, i.e. is an inner row
						if (bIsInnerRow) {
							if (pageBeginning > end || pageEnd < beginning) {
								// if beginning of the page more than end of the inner row, then clone an empty row
								bRemoveRow = true;
							} else {
								// if beginning of the page is before the start of inner row first cell, then put inner row first cell as the beginning
								innerRowBeginning = pageBeginning < beginning ? beginning : pageBeginning;
								// if end of the page is after the last cell in inner row, then put last cell in inner row as the end.
								innerRowEnd = pageEnd > end ? end : pageEnd;

								firstCell = csiTable.getCellAtIndex(innerRow, innerRowBeginning);

								if (!firstCell) {
									firstCell = csiTable.getCellAtIndex(innerRow, innerRowBeginning, {bReturnLastCell: true});
									firstCell = firstCell.nextSibling;
								}

								innerRowBeginning = firstCell.cellIndex;
								innerRowEnd = csiTable.getCellAtIndex(innerRow, innerRowEnd, {bReturnLastCell: true}).cellIndex;
							}
						}

						rowPageBoundary.push(new _RowPageBoundary(innerRowBeginning, innerRowEnd, bRemoveRow));

						pageBeginning = pageEnd + 1;
					}

					this.rowColumnMap.push(new _Row(rowPageBoundary, beginning, end, innerRow.offsetHeight));
				}
			}
		},

		/**
		 * This method adds refNodes back to the view. And sets height of each row to 1
		 * This is needed to make sure that all rows are just using as little height as they need
		 * @param {DOMNode[]} refNodes - consists all rows in the table
		 * @param {DOMNode} eContainer - the current view
		 */
		_addRefNodesToView: function(refNodes, eContainer) {
			_.each(refNodes, function(refNode) {
				eContainer.appendChild(refNode);
			});
		},

		/**
		 * Populates this.headers with meta data about each header.
		 * @param {Number} nPageWidth - maximum paper width
		 * @param {DOMNode} eView - root print view (may be root node of Html view)
		 */
		_buildColumnWidthMap: function(nPageWidth, eView) {
			const {headers, headerNodes, subHeaderNodes} = this;
			var eTable = this._getTableViewNode(eView);
			var bIsInMatrix;
			var bCombineMatrixWithNext;
			var firstSubHeaderIndex;
			var header;
			var headerIndex;
			var subHeaderIndex = 0;
			var totalWidth = 0;
			var totalWidthBeforeMatrix;
			var nFrozenColumns = csiTable.getNumberOfFrozenColumns(eTable);
			var startingWidth = 0;
			var minWidth;
			var matrixHeaderHeight;
			var matrixMinWidth;
			var matrixTotalWidth;
			var matrixWidthOnPage;
			var matrixWidthOnPreviousPage;
			var halfPageWidth = nPageWidth / 2;
			var pageSeparateWidth = Math.ceil(nPageWidth * this.HORIZONTAL_SPLIT_FULL_MATRIX_PCT);
			var bSplitMatrix;
			let combineWidth;
			let startHeaderIndex;

			for (headerIndex = 0; headerIndex < headerNodes.length; headerIndex++) {
				header = headerNodes[headerIndex];
				bIsInMatrix = csiTable.isMatrixHeader(header);

				if (bIsInMatrix) {
					if (!bCombineMatrixWithNext) {
						totalWidthBeforeMatrix = totalWidth;
						combineWidth = 0;
						matrixTotalWidth = 0;
						matrixWidthOnPage = 0;
						matrixWidthOnPreviousPage = 0;
						startHeaderIndex = headers.length - 1;
					}

					firstSubHeaderIndex = subHeaderIndex;
					bCombineMatrixWithNext = domClass.contains(header, csiTable.CSS_MATRIX_2ND_DIMENSION);
					bSplitMatrix = false;

					if (bCombineMatrixWithNext) {
						combineWidth = subHeaderNodes[subHeaderIndex++].offsetWidth;
						headers.push(new _Header(bIsInMatrix, headerIndex, combineWidth, firstSubHeaderIndex));
					} else {
						// find minimum width of matrix header
						matrixHeaderHeight = $(header).height();
						matrixMinWidth = Math.max(combineWidth + _getMinWidthForPrimaryHeader(header, matrixHeaderHeight), this.MIN_MATRIX_WIDTH_PER_PAGE);

						// ensure that matrix header does not expand in height and hide's overflow
						$(header).wrapInner('<div class="ellipsis"></div>');
						$(header).children().css("max-height", matrixHeaderHeight + "px");

						// add matrix column's sub headers to headers array
						for (let i = 0, columnIndex = headers.length - 1; i < header.colSpan; ++i, ++columnIndex) {
							// add a matrix column to the headers
							minWidth = subHeaderNodes[subHeaderIndex++].offsetWidth;
							headers.push(new _Header(bIsInMatrix, headerIndex, minWidth, firstSubHeaderIndex));

							// if combining with previous matrix, add its width now and reset (so only added once)
							minWidth += combineWidth;
							combineWidth = 0;

							// if exceeded page width, then start new horizontal page
							if ((totalWidth + minWidth) > nPageWidth) {
								matrixWidthOnPreviousPage = matrixWidthOnPage;
								matrixWidthOnPage = 0;

								if (matrixTotalWidth < matrixMinWidth) {
									// if not enough columns on first page to show matrix header, then move whole matrix to next page
									this.colsPerPage.push(startHeaderIndex);
									this.widthPerPage.push(totalWidthBeforeMatrix);
									totalWidth = startingWidth + matrixTotalWidth;
								} else {
									// split matrix at current subheader
									this.colsPerPage.push(columnIndex);
									this.widthPerPage.push(totalWidth);
									totalWidth = startingWidth;
									bSplitMatrix = true;
								}
							}

							matrixTotalWidth += minWidth;
							matrixWidthOnPage += minWidth;
							totalWidth += minWidth;
						}
					}

					if (bSplitMatrix) {
						var nPage = this.colsPerPage.length - 1;

						if ((totalWidthBeforeMatrix > pageSeparateWidth) && ((startingWidth + matrixTotalWidth) <= nPageWidth)) {
							// if matrix could fit on its own page, and it was split near the start of the matrix, then move it to its own page
							this.colsPerPage[nPage] = startHeaderIndex;
							this.widthPerPage[nPage] = totalWidthBeforeMatrix;
							totalWidth = startingWidth + matrixTotalWidth;
						} else if (matrixWidthOnPage < matrixMinWidth) {
							// matrix has too few columns on this page to fit main header, so change split in matrix if previous page has matrix columns to spare
							var matrixSplitIndex = this.colsPerPage.last();
							var totalWidthOnPreviousPage = this.widthPerPage.last();

							minWidth = headers[matrixSplitIndex].width;

							while (((matrixWidthOnPreviousPage - minWidth) > matrixMinWidth) && (matrixWidthOnPage < matrixMinWidth)) {
								matrixWidthOnPreviousPage -= minWidth;
								totalWidthOnPreviousPage -= minWidth;

								matrixWidthOnPage += minWidth;
								totalWidth += minWidth;

								--matrixSplitIndex;
								minWidth = headers[matrixSplitIndex].width;
							}

							this.colsPerPage[nPage] = matrixSplitIndex;
							this.widthPerPage[nPage] = totalWidthOnPreviousPage;
						}
					}
				} else {
					// regular header - not in matrix
					minWidth = headerNodes[headerIndex].offsetWidth;

					// if exceeded page width, then start new horizontal page
					if ((totalWidth + minWidth) > nPageWidth) {
						this.colsPerPage.push(headers.length - 1);
						this.widthPerPage.push(totalWidth);
						totalWidth = startingWidth;
					}

					totalWidth += minWidth;
					headers.push(new _Header(false, headerIndex, minWidth));

					// track frozen columns and their total width; disregard if matrix is within frozen column count or if frozen columns exceed half the page width
					if (nFrozenColumns > 0 && headerIndex < nFrozenColumns) {
						if (!matrixTotalWidth && (!startingWidth || (startingWidth + minWidth <= halfPageWidth))) {
							// add current column to frozen columns
							startingWidth += minWidth;
							this.frozenColIndex.push(headerIndex);
						} else {
							// stop tracking frozen columns if rules fail for current column
							nFrozenColumns = 0;
						}
					}
				}
			}

			this.colsPerPage.push(headers.length - 1);
			this.widthPerPage.push(totalWidth);

			// adjust column widths based on extra width per page
			this._adjustColsWidth(this._getColGroupNode(eView), nPageWidth);

			// wrap any comment rows to control page width
			$(eView).find(`.${DATA_SHEET_COMMENT_CLASS} > td`).wrapInner(`<div style="width: calc(${nPageWidth}px - 2 * var(--TableCellPaddingH))"></div>`);
		},

		/**
		 * Expand columns per page to exactly equal the page width, except for the last page where there is a minimum width to
		 * ensure that footer message fits within the table. Frozen columns are not changed.
		 * @param {DOMNode} colgroup
		 * @param {number} nPageWidth
		 */
		_adjustColsWidth: function(colgroup, nPageWidth) {
			var cols = colgroup.childNodes;
			var nFrozenColumns = this.frozenColIndex.length;
			var startIndex = nFrozenColumns;
			var nSplits = this.colsPerPage.length - 1;

			for (var splitIndex = 0; splitIndex <= nSplits; ++splitIndex) {
				const endIndex = this.colsPerPage[splitIndex];
				const availableWidth = (splitIndex === nSplits) ? this.minTableWidth : nPageWidth;
				const extraWidth = availableWidth - this.widthPerPage[splitIndex];

				if (extraWidth > 0) {
					var nColumns = endIndex - startIndex + 1;
					var diff = Math.floor(extraWidth / nColumns);
					var remainder = extraWidth - diff * nColumns;

					for (var columnIndex = startIndex; columnIndex <= endIndex; ++columnIndex) {
						if (columnIndex == endIndex) {
							diff += remainder;
						}

						if (diff) {
							this.headers[columnIndex].width += diff;
							$(cols[columnIndex]).width(this.headers[columnIndex].width);
						}
					}

					this.widthPerPage[splitIndex] = availableWidth;
				}

				startIndex = endIndex + 1 + nFrozenColumns;
			}

			// ensure that column widths are honored by setting table width
			var totalWidth = _.reduce(this.widthPerPage, (memo, w) => memo + w, 0);
			$(colgroup.parentNode).width(totalWidth);
		},

		/**
		 * This method splits the headings only, and creates a new container per split required.
		 */
		_splitHeadings: function(aPages, eView, eContainer, copyCallback, nPageWidth) {
			// first we need to find out how many containers we need to split the headings to.
			var beginning = 0;
			var end;

			for (var i = 0; i < this.colsPerPage.length; i++) {
				end = this.colsPerPage[i];

				this._splitContainerHorizontally(aPages, eView, eContainer, copyCallback, beginning, end, i);

				beginning = this.colsPerPage[i] + 1;
			}
		},

		/**
		 * This method gets the source cells and destination then copies from source and puts it
		 * in the destination row. Number of cells to be copied are read from this.frozenColIndex.
		 *
		 * @param {DOMNode[]} srcCells - Cells in the source row
		 * @param {DOMNode[]} destCells - Cells in the destination row
		 * @param {Boolean} bMainHeader - if the rows' cells passed are for a main header
		 */
		_copyFrozenColumnsToEachPageForRow: function(srcCells, destCells, bMainHeader) {
			var clonedCell;
			var index;
			var firstDestCell = $(destCells[0]);
			_.each(this.frozenColIndex, function(frozenColIndex) {
				// clone frozen columns
				index = frozenColIndex;
				if (bMainHeader) {
					index = this.headers[index].mainHeaderIndex;
				}
				clonedCell = html.cloneElement(srcCells[index]);
				// insert the frozen columns into beginning of next page
				firstDestCell.before(clonedCell);
			}, this);
		},

		/**
		 * Remove columns from table row or colgroup that are not frozen and not in left to right range
		 * @param {DOMNode} row - could also be a colgroup
		 * @param {DOMNode} subrow - row containing spanned cells if row spans two rows as in matrix case
		 * @param {number} nFrozen - number of frozen columns
		 * @param {number} nLeft - index of left column in display range
		 * @param {number} nRight - index of right column in display range
		 * @param {boolean} bHeadRow - true if row is in header (as opposed to colgroup)
		 */
		_splitRowHorizontally: function(row, subrow, nFrozen, nLeft, nRight, bHeadRow) {
			const cssMatrixFirst = "csi-table-matrix-column-first";
			const cssMatrixLast = "csi-table-matrix-column-last";
			const cells = [...row.children];
			const subcells = subrow && [...subrow.children];
			const columns = cells.length;
			const headers = this.headers;
			let headerIndex = 0;

			const removeSubs = (subIndex, n, bFirst) => {
				for (let i = 0; i < n; ++i) {
					subrow.removeChild(subcells[subIndex++]);
				}

				if (bFirst) {
					subcells[subIndex].classList.add(cssMatrixFirst);
				}
			};

			nLeft = Math.max(nLeft, nFrozen);

			for (let i = 0; i < columns; ++i) {
				const subheaderIndex = headers[headerIndex].firstSubHeaderIndex;
				const hasSubheader = subrow && (subheaderIndex != null);
				const th = cells[i];
				const n = th.colSpan || 1;	// <col> has no colSpan, so default to 1
				const j = headerIndex + n - 1;

				if (j < nFrozen) {
					// keep column and all subheaders
					if (bHeadRow && (j == nFrozen - 1) && (nLeft != nFrozen)) {
						// if there is a gap between the frozen column and the left column, then add CSS for double border;
						// we are reusing the CSS for a double-border at the start of a matrix, but this applies to non-matrix columns too
						th.classList.add(cssMatrixLast);

						if (hasSubheader) {
							subcells[subheaderIndex + n - 1].classList.add(cssMatrixLast);
						}
					}
				} else if ((headerIndex < nLeft) && (j >= nLeft)) {
					// reduce span of column and remove subheaders from front and possibly back
					const removeFromLeft = nLeft - headerIndex;
					const removeFromRight = j - nRight;

					th.colSpan = Math.min(j, nRight) - nLeft + 1;
					removeSubs(subheaderIndex, removeFromLeft, true);

					if (removeFromRight > 0) {
						removeSubs(subheaderIndex + removeFromLeft + th.colSpan, removeFromRight);
					}
				} else if ((headerIndex <= nRight) && (j > nRight)) {
					// reduce span of column and remove subheaders from back
					th.colSpan = nRight - headerIndex + 1;
					removeSubs(subheaderIndex + th.colSpan, j - nRight);
				} else if ((headerIndex < nLeft) || (headerIndex > nRight)) {
					// remove column and all subheaders
					row.removeChild(th);

					if (hasSubheader) {
						removeSubs(subheaderIndex, n);
					}
				}

				headerIndex = j + 1;
			}
		},

		/**
		 * 1. Makes a copy of the heading section of the body container passed. Keeps only the headings within
		 * the range passed to this method on the copied container.
		 * 2. Puts the copied container in this.containers array so we can use it later to assign each column in each row
		 * to the right container.
		 * 3. Puts the right colSpan for the footer so we don't end up with banana looking footer.
		 * 4. Sets the page width to the wrapper of the container as it's by default bigger than the table.
		 */
		_splitContainerHorizontally: function(aPages, eView, eContainer, copyCallback, beginning, end, pageIndex) {
			// if it's the first time we're copying the original page, we shouldn't call copyCallBack because we need to keep
			// all blocks in copied page.
			const bFirstPage = !pageIndex;
			const container = this._copyPageWithContainer(aPages, eView, eContainer, true, bFirstPage ? null : copyCallback);
			const colgroup = this._getColGroupNode(container.viewNode);
			const thead = this._getHeadNode(container.viewNode);
			const headRows = [...thead.children];
			const tfoot = $(container.containerNode).next("tfoot");
			const table = colgroup.parentNode;
			const nFrozen = this.frozenColIndex.length;

			this._splitRowHorizontally(colgroup, null, nFrozen, beginning, end);

			if (this.subHeaderNodes) {
				this._splitRowHorizontally(headRows.shift(), headRows.shift(), nFrozen, beginning, end, true);
			}

			headRows.forEach(row => {
				this._splitRowHorizontally(row, null, nFrozen, beginning, end, true);
			});

			this.containers.push(container);

			if (tfoot.length) {
				// colSpan needs to be changed to number of columns per page.
				tfoot.children()[0].firstChild.colSpan = colgroup.children.length;
			}

			const width = this.widthPerPage[pageIndex];
			html.setPixels(table, html.WIDTH, width);
			html.setPixels(table, html.MINWIDTH, width);
		},

		/**
		 * applies the rows to the correct container with the right heading
		 *
		 * @param {DOMNode[]} aPages - an array of pages.
		 * @param {DOMNode} eView - is a single page, contains everything in the table: header, body, footer
		 * @param {DOMNode} eContainer - has only the <body> tag of the table. At this point, it's empty
		 * @param {Function} [copyCallback] - value is optional
		 * @param {number} nPageHeight - height of the page minus print margins.
		 * @param {number} otherPageHeightOverhead -
		 * @param {number} firstPageHeightOverhead - is the height of the body of the table with all rows in it.
		 * @param {Function} [copyCallback] - value is optional
		 * @param {DOMNode} viewRoot - root of the table with all its content
		 * @param {DOMNode} refNodes - deattached table body content, i.e. all rows excluding the headings.
		 */
		_applyRowsToContainers: function(aPages, eView, eContainer, copyCallback, nPageHeight, otherPageHeightOverhead, firstPageHeightOverhead, viewRoot, refNodes) {
			const {containers, rowMap, _pageBoundaryRows} = this;
			let n = 0;

			for (let i = 0; i < this.nRows; i++) {
				// advance to next page if height exceeds available space on page and either this is not the first page or
				// the next page won't have any more space either
				if (_pageBoundaryRows[i]) {
					// if we have too many rows for a page, we need to make copies of the heading containers equals to number of
					// pages required for splitting of columns horizontally.
					for (let j = 0; j < this.colsPerPage.length; j++) {
						const copyResult = this._copyPageWithContainer(aPages, containers[j].viewNode, containers[j].containerNode, true, copyCallback);
						containers.push(copyResult);
					}
				}

				// once the row containers are pushed to this.containers, we need to assign each row to all column containers.
				for (let j = containers.length - this.colsPerPage.length; j < containers.length; j++) {
					this.rowContainers[n++] = containers[j].containerNode;
				}
			}

			// for splitting, we copied the main view over and over and made our fixes on the copies,
			// so to avoid having the main view in the print view, we have to remove it.
			html.destroyElement(eView);

			var refNodeClone;
			var rowPageBoundary;
			var nFrozenColumns = this.frozenColIndex.length;
			var bIsDivider;
			var bIsGroup;
			var bIsGroupNoReport;
			var bIsPrimary;
			var bIsDataSheetComment;
			var bIsReport;
			var dividerColSpan;

			// add rows to appropriate page
			for (var containerCounter = 0, rowCounter = 0; rowCounter < this.nRows; rowCounter++) {
				for (var page = 0; page < this.colsPerPage.length; page++, containerCounter++) {
					for (var innerRowCounter = rowMap[rowCounter]; innerRowCounter < rowMap[rowCounter + 1]; ++innerRowCounter) {
						// we don't want to touch the original
						// so clone refNodes and slice it up based on colsPerPage so next loop we can have the original refNode.
						bIsDataSheetComment = $(refNodes[innerRowCounter]).hasClass(DATA_SHEET_COMMENT_CLASS);

						refNodeClone = html.cloneElement(refNodes[innerRowCounter]);
						rowPageBoundary = this.rowColumnMap[innerRowCounter].rowPageBoundary[page];

						bIsDivider = $(refNodes[innerRowCounter]).hasClass(DIVIDER_CLASS);
						bIsGroup = $(refNodes[innerRowCounter]).hasClass(GROUP_CLASS);
						bIsReport = $(refNodes[innerRowCounter]).hasClass(REPORT_CLASS);
						bIsGroupNoReport = (bIsGroup && !bIsReport);

						if (bIsDivider || bIsGroupNoReport || bIsDataSheetComment) {
							// When row is a group and there's no report row, then it gets colspan equals to number of columns (similar to dividers)
							// Otherwise, group row would look like a normal row (would have cells equals to number of columns)
							if (page == 0) {
								dividerColSpan = this.colsPerPage[page] + 1;
							} else {
								dividerColSpan = this.colsPerPage[page] - this.colsPerPage[page - 1];

								if (nFrozenColumns > 0) {
									// If we have frozen columns, then add them as colspan to the divider
									dividerColSpan += nFrozenColumns;
								} else {
									// If there's no frozen columns, requirement says we should not repeat
									// content of these types of rows when paginating horizontally
									$(refNodeClone.children[0]).empty();
								}
							}

							refNodeClone.children[0].colSpan = dividerColSpan;
						} else if (rowPageBoundary.bRemoveRow) {
							// if beginning of the page more than end of the inner row, then clone an empty row
							$(refNodeClone).empty();
						} else {
							this._removeAllChildrenExceptGivenRange(refNodeClone, rowPageBoundary.beginning, rowPageBoundary.end);
						}

						this.rowContainers[containerCounter].appendChild(refNodeClone);

						bIsPrimary = $(refNodes[innerRowCounter]).hasClass(PRIMARY_ROW_CLASS);

						// Frozen column are not added to first page, or to rows that span the whole view
						if (page != 0 && !bIsDivider && !bIsGroupNoReport && !bIsDataSheetComment && nFrozenColumns > 0) {
							// add CSS for double border to first child of row that will be first cell after frozen columns
							$(refNodeClone.firstChild).addClass("csi-table-matrix-column-first");

							if (bIsPrimary || bIsReport) {
								// Frozen columns only need to be added to primary or report rows - especially not matrix merged rows
								this._copyFrozenColumnsToEachPageForRow(refNodes[innerRowCounter].childNodes, refNodeClone.childNodes);
							}
						}
					}
				}
			}

			// If the copied block doesn't have any rows in it, we clean it up. This is done post row assignment to ensure shifted
			// or split row matrices aren't removed.
			if (containers[0].containerNode.childElementCount < 1) {
				$(".csiPageBlock", containers[0].viewNode).has(containers[0].containerNode).remove();
			}
		},

		/**
		 * @return {Boolean} If both height and width are within the paper size, then no splitting is required and it returns true.
		 * Otherwise false.
		 */
		_splittingNotRequired: function(eView, contentWidth, nPageHeight, nPageWidth) {
			return Math.floor(eView.offsetHeight) <= Math.floor(nPageHeight) && Math.floor(contentWidth) <= Math.floor(nPageWidth);
		},

		/**
		 * For the case where table fits on the paper, then we need to prepare some data for footer.
		 * @param {DOMNode} eView
		 * @param {DOMNode} eContainer
		 * @param {Number} nPageWidth - page width
		 */
		_prepareDataForSinglePagePrint: function(eView, eContainer, nPageWidth) {
			this.containers = [{viewNode: eView, containerNode: eContainer}];
			this.colsPerPage[0] = this.numberOfColumns;
			var tfoot = $(eContainer).next("tfoot");
			if (tfoot.length) {
				tfoot.children()[0].firstChild.colSpan = this.colsPerPage[0];
			}
		},

		/**
		 * Return colgroup node of table view given the root node of the print view, which may or may not be the table
		 * @param {DOMNode} eView
		 * @return {DOMNode}
		 */
		_getColGroupNode: function(eView) {
			return $(eView).find(".csi-view-table > colgroup").last()[0];
		},

		/**
		 * Return tbody node of table view given the root node of the print view, which may or may not be the table
		 * @param {DOMNode} eView
		 * @return {DOMNode}
		 */
		_getContainerNode: function(eView) {
			return $(eView).find(".csi-view-table > tbody").last()[0];
		},

		/**
		 * Returns the last table header found in the given view, as the last view is always the one being split.
		 * This is used in cases where the view passed in contains more than one table (i.e. compositions).
		 * @param  {DOMNode} eView - the view containing a table
		 * @return {DOMNode} - the header node
		 */
		_getHeadNode: function(eView) {
			return $(eView).find(".csi-view-table > thead").last()[0];
		},

		/**
		 * Return root node of table view given the root node of the print view, which may or may not be the table
		 * @param {DOMNode} eView
		 * @return {DOMNode}
		 */
		_getTableViewNode: function(eView) {
			return $(eView).find(".tableViewRoot").last()[0] || eView;
		},

		/**
		 * In a composite view, if page is too wide and needs horizontal splitting OR if headers with one
		 * row of body can't fit in the page, then we should put the table in a new page
		 * and remove it from last page. That's because in html/Widget when we are processing composite printing
		 * we by default add the block to the last page and leave it to individual splitters to decide if they need
		 * a new page.
		 *
		 * @param {DOMNode} aPages - contains all pages
		 * @param {DOMNode} eView - this is the last page
		 * @param {Number} nPageWidth - page width
		 * @param {Number} contentWidth - content width
		 * @param {Number} nPageHeight - page height
		 * @param {DOMNode} refNodes - consists all rows in the table
		 */
		_prepareFirstPage: function(aPages, eView, eContainer, nPageWidth, contentWidth, nPageHeight, refNodes) {
			var cssQuery = "." + html.CSS_PAGE;
			var nBlocks = $(eView).find(cssQuery).length;
			var cssCurrentBlock = ".csi-split-current-block";

			// add one row back to the body of the table
			if (refNodes.length != 0) {
				eContainer.appendChild(refNodes[0]);

				// get the height of the entire page content again.
				var pageContentHeight = eView.offsetHeight;

				html.removeRange(refNodes[0]);

				// see if table is too wide OR if height of entire page is more than the paper height
				if ((Math.floor(contentWidth) > Math.floor(nPageWidth) || pageContentHeight > nPageHeight) && nBlocks > 1) {
					// Remove current block from eView(last page)
					var currentBlock = $(eView).find(cssCurrentBlock).last();
					currentBlock.remove();

					// make a copy of the eView
					var eCopy = html.cloneElement(eView);

					var blockParent = $(eCopy).find("." + html.CSS_PAGE)[0].parentNode;
					// remove all blocks from the copied page, so we don't get repeated blocks
					$(eCopy).find(cssCurrentBlock).remove();
					// then add the current block to the new page.
					blockParent.appendChild(currentBlock[0]);

					// add the new page to parent of all pages.
					eView.parentNode.appendChild(eCopy);
					aPages.push(eCopy);
					// pass the current block as the eView for splitting.
					$(eCopy).find(cssCurrentBlock).removeClass(cssCurrentBlock);

					// put eView equals to the copy
					eView = eCopy;
					eContainer = this._getContainerNode(eView);
				}
			}

			return {view: eView, container: eContainer};
		}
	});

	printUtil.registerViewSplitter("table", Splitter);

	return Splitter;
});